import { useCallback, useMemo, useState, Fragment } from "react";

import {
  Button,
  Card,
  Icon,
  Input,
  Notification,
  Select,
} from "@stellar/design-system";

import { useRecoveryAuditReport, RecoveryAuditReportSearchParams } from "@/apiQueries/useRecoveryAuditReport";
import { useRecoveryAuditReportExport, RecoveryAuditExportParams } from "@/apiQueries/useRecoveryAuditReportExport";
import { useRecoveryAuditTransactionHistory } from "@/apiQueries/useRecoveryAuditTransactionHistory";
import { ErrorWithExtras } from "@/components/ErrorWithExtras";
import { InfoTooltip } from "@/components/InfoTooltip";
import { Pagination } from "@/components/Pagination";
import { Table } from "@/components/Table";
import { formatDateTime } from "@/helpers/formatIntlDateTime";
import { getSdpTenantName } from "@/helpers/getSdpTenantName";
import { shortenReceiverId } from "@/helpers/shortenReceiverId";
import { shortenString } from "@/helpers/shortenString";
import { useRedux } from "@/hooks/useRedux";
import { SortDirection } from "@/types";

import "./styles.scss";

const PAGE_LIMIT = 20;
const SUPPORTED_SORT_FIELDS = [
  "userStellarAccount",
  "usdcBalanceSnapshot",
  "latestStatus",
  "lastEventAt",
  "lastActorRole",
] as const;

type SupportedSortField = (typeof SUPPORTED_SORT_FIELDS)[number];

export const RecoveryAuditCard = () => {
  const { organization } = useRedux("organization");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<SupportedSortField>("lastEventAt");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [status, setStatus] = useState("");
  const [accountId, setAccountId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [expandedRowId, setExpandedRowId] = useState<number | undefined>(undefined);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const organizationName = useMemo(() => {
    const tenantName = getSdpTenantName(organization.data.name);
    return tenantName || undefined;
  }, [organization.data.name]);

  const searchParams: RecoveryAuditReportSearchParams = useMemo(
    () => ({
      page: currentPage,
      limit: PAGE_LIMIT,
      sortBy,
      sortDirection,
      organizationName,
      accountId: accountId.trim() || undefined,
      status: status || undefined,
      from: fromDate ? `${fromDate}T00:00:00.000Z` : undefined,
      to: toDate ? `${toDate}T23:59:59.999Z` : undefined,
    }),
    [currentPage, sortBy, sortDirection, organizationName, accountId, status, fromDate, toDate],
  );

  const { data, isLoading, isFetching, error } = useRecoveryAuditReport(searchParams);
  const {
    mutateAsync: exportCsv,
    isPending: isExportPending,
    error: exportError,
  } = useRecoveryAuditReportExport();
  const { data: detail, isLoading: isDetailLoading } =
    useRecoveryAuditTransactionHistory(expandedRowId);

  const items = data?.data ?? [];
  const pagination = data?.pagination;
  const maxPages = Math.max(1, pagination?.totalPages ?? 1);
  const hasRows = items.length > 0;

  const onSort = useCallback((field: SupportedSortField) => {
    setCurrentPage(1);

    if (sortBy === field) {
      setSortDirection((prev) => (prev === "desc" ? "asc" : "desc"));
      return;
    }

    // Keep newly selected columns aligned with report default: newest first.
    setSortBy(field);
    setSortDirection("desc");
  }, [sortBy]);

  const getColumnSortDirection = useCallback((field: SupportedSortField): SortDirection => {
    return sortBy === field ? sortDirection : "default";
  }, [sortBy, sortDirection]);

  const toggleRow = (id: number) => {
    setExpandedRowId((prev) => (prev === id ? undefined : id));
  };

  const toggleSelect = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const currentPageIds = useMemo(() => new Set(items.map((i) => i.transactionId)), [items]);

  const allCurrentPageSelected = useMemo(
    () => currentPageIds.size > 0 && [...currentPageIds].every((id) => selectedIds.has(id)),
    [currentPageIds, selectedIds],
  );

  const toggleSelectAll = useCallback(() => {
    if (allCurrentPageSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        currentPageIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        currentPageIds.forEach((id) => next.add(id));
        return next;
      });
    }
  }, [allCurrentPageSelected, currentPageIds]);

  const handleExport = async () => {
    const params: RecoveryAuditExportParams = {
      ...searchParams,
      transactionIds: selectedIds.size > 0 ? Array.from(selectedIds) : undefined,
    };
    await exportCsv(params);
  };

  const hasFilters = !!status || !!accountId.trim() || !!fromDate || !!toDate;

  // Hide the entire card when there are no audit events and no filters applied
  const isEmpty = !isLoading && !isFetching && !error && items.length === 0 && !hasFilters;
  if (isEmpty) {
    return null;
  }

  const exportLabel = selectedIds.size > 0
    ? `Export Selected (${selectedIds.size})`
    : "Export All";

  return (
    <Card>
      <div className="CardStack__card RecoveryAuditCard">
        <div className="CardStack__title">
          <InfoTooltip infoText="Review account recovery lifecycle events, sort and export audit data.">
            Account Recovery Audit
          </InfoTooltip>
        </div>

        <div className="RecoveryAuditCard__filters">
          <Select
            id="recovery-audit-status"
            fieldSize="sm"
            label="Status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value=""></option>
            <option value="PENDING">PENDING</option>
            <option value="ORG_SIGNED">ORG_SIGNED</option>
            <option value="VIA_SIGNED">VIA_SIGNED</option>
            <option value="FULLY_SIGNED">FULLY_SIGNED</option>
            <option value="SUBMITTED">SUBMITTED</option>
            <option value="SUCCESS">SUCCESS</option>
            <option value="FAILED">FAILED</option>
            <option value="CANCELLED">CANCELLED</option>
          </Select>

          <Input
            id="recovery-audit-account-id"
            label="Account ID"
            fieldSize="sm"
            placeholder="Contains match"
            value={accountId}
            onChange={(e) => {
              setAccountId(e.target.value);
              setCurrentPage(1);
            }}
          />

          <Input
            id="recovery-audit-from-date"
            label="From date"
            fieldSize="sm"
            type="date"
            value={fromDate}
            onChange={(e) => {
              setFromDate(e.target.value);
              setCurrentPage(1);
            }}
          />

          <Input
            id="recovery-audit-to-date"
            label="To date"
            fieldSize="sm"
            type="date"
            value={toDate}
            onChange={(e) => {
              setToDate(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <div className="RecoveryAuditCard__actions">
          <Button
            size="md"
            variant="tertiary"
            onClick={() => {
              setStatus("");
              setAccountId("");
              setFromDate("");
              setToDate("");
              setSelectedIds(new Set());
              setCurrentPage(1);
            }}
            disabled={!hasFilters && selectedIds.size === 0}
          >
            Reset
          </Button>

          <Button
            size="md"
            variant="secondary"
            icon={<Icon.Download01 />}
            onClick={handleExport}
            isLoading={isExportPending}
            disabled={isLoading || isFetching}
          >
            {exportLabel}
          </Button>
        </div>

        {error ? (
          <Notification variant="error" title="Error" isFilled>
            <ErrorWithExtras appError={error} />
          </Notification>
        ) : null}

        {exportError ? (
          <Notification variant="error" title="Error" isFilled>
            <ErrorWithExtras appError={exportError} />
          </Notification>
        ) : null}

        {hasRows ? (
          <>
            <Card noPadding>
              <Table isLoading={isLoading || isFetching} isScrollable>
                <Table.Header>
                  <Table.HeaderCell width="2rem">
                    <input
                      type="checkbox"
                      aria-label="Select all recovery audit rows on this page"
                      checked={allCurrentPageSelected}
                      onChange={toggleSelectAll}
                    />
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sortDirection={getColumnSortDirection("userStellarAccount")}
                    onSort={() => onSort("userStellarAccount")}
                  >
                    User Account
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sortDirection={getColumnSortDirection("usdcBalanceSnapshot")}
                    onSort={() => onSort("usdcBalanceSnapshot")}
                  >
                    USDC
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sortDirection={getColumnSortDirection("latestStatus")}
                    onSort={() => onSort("latestStatus")}
                  >
                    Status
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sortDirection={getColumnSortDirection("lastEventAt")}
                    onSort={() => onSort("lastEventAt")}
                  >
                    Last Event At
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sortDirection={getColumnSortDirection("lastActorRole")}
                    onSort={() => onSort("lastActorRole")}
                  >
                    Actor
                  </Table.HeaderCell>
                  <Table.HeaderCell width="6rem">Timeline</Table.HeaderCell>
                </Table.Header>
                <Table.Body>
                  {items.map((item) => {
                    const isExpanded = expandedRowId === item.transactionId;
                    return (
                      <Fragment key={item.transactionId}>
                        <Table.BodyRow className="RecoveryAuditCard__row">
                          <Table.BodyCell>
                            <input
                              type="checkbox"
                              aria-label={`Select recovery audit row ${item.transactionId}`}
                              checked={selectedIds.has(item.transactionId)}
                              onChange={() => toggleSelect(item.transactionId)}
                            />
                          </Table.BodyCell>
                          <Table.BodyCell title={item.userStellarAccount}>
                            <span className="RecoveryAuditCard__cellWrap">
                              {shortenReceiverId(item.userStellarAccount)}
                            </span>
                          </Table.BodyCell>
                          <Table.BodyCell>{item.usdcBalanceSnapshot ?? "—"}</Table.BodyCell>
                          <Table.BodyCell>{item.latestStatus}</Table.BodyCell>
                          <Table.BodyCell>{formatDateTime(item.lastEventAt)}</Table.BodyCell>
                          <Table.BodyCell>{item.lastActorRole}</Table.BodyCell>
                          <Table.BodyCell>
                            <Button
                              variant="tertiary"
                              size="sm"
                              onClick={() => toggleRow(item.transactionId)}
                            >
                              {isExpanded ? "Hide" : "Timeline"}
                            </Button>
                          </Table.BodyCell>
                        </Table.BodyRow>
                        {isExpanded && (
                          <tr key={`timeline-${item.transactionId}`}>
                            <td colSpan={7} className="RecoveryAuditCard__inlineTimeline">
                              {isDetailLoading ? (
                                <span className="Note">Loading timeline…</span>
                              ) : (detail?.timeline || []).length === 0 ? (
                                <span className="Note">No timeline events</span>
                              ) : (
                                <div className="RecoveryAuditCard__timelineList">
                                  {(detail?.timeline || []).map((event) => (
                                    <div key={event.eventId} className="RecoveryAuditCard__timelineEvent">
                                      <div className="RecoveryAuditCard__timelineHeader">
                                        <strong>{event.eventType}</strong>
                                        <span className="RecoveryAuditCard__timelineDate">
                                          {formatDateTime(event.occurredAt)}
                                        </span>
                                      </div>
                                      <div className="RecoveryAuditCard__timelineMeta">
                                        Status: {event.statusSnapshot}
                                      </div>
                                      <div className="RecoveryAuditCard__timelineMeta">
                                        Actor: {event.actorRole}
                                        {event.actorId ? ` (${shortenString(event.actorId, 6)})` : ""}
                                      </div>
                                      {event.metadata && Object.keys(event.metadata).length > 0 && (
                                        <div className="RecoveryAuditCard__timelineMetadata">
                                          {Object.entries(event.metadata).map(([key, value]) => (
                                            <div key={key} className="RecoveryAuditCard__timelineMetaItem">
                                              {key}: {typeof value === "string" && value.length > 20
                                                ? shortenString(value, 8)
                                                : String(value)}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </Table.Body>
              </Table>
            </Card>

            <div className="RecoveryAuditCard__pagination">
              <Pagination
                currentPage={currentPage}
                maxPages={maxPages}
                onSetPage={(page) => setCurrentPage(page)}
                isLoading={isLoading || isFetching}
              />
            </div>
          </>
        ) : null}
      </div>
    </Card>
  );
};
